import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ProposePlayModal from './ProposePlayModal';
import { PLAY_FORMATS, PLAY_TYPES } from '../constants';
import { formatTime } from '../utils/dateTimeUtils';
import { supabase } from '../config/supabaseClient';

export default function OpenProposalsSection({ proposals, isLoading = false, onProposalDeleted }) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [error, setError] = useState(null);

  const handleEdit = (proposal) => {
    setSelectedProposal(proposal);
    setShowEditModal(true);
  };

  const handleDeleteClick = (proposal) => {
    setProposalToDelete(proposal);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // First delete all participants
      const { error: participantsError } = await supabase
        .from('proposal_participants')
        .delete()
        .eq('proposal_id', proposalToDelete.id);

      if (participantsError) {
        throw participantsError;
      }

      // Then delete the proposal
      const { error: proposalError } = await supabase
        .from('play_proposals')
        .delete()
        .eq('id', proposalToDelete.id);

      if (proposalError) {
        throw proposalError;
      }

      // Call the callback to refresh the proposals list
      if (onProposalDeleted) {
        onProposalDeleted();
      }
    } catch (error) {
      console.error('Error deleting proposal:', error);
      setError(error.message);
    } finally {
      setShowDeleteConfirm(false);
      setProposalToDelete(null);
    }
  };

  const handleEditSubmit = (formData) => {
    // In production, this would be an API call
    console.log('Editing proposal:', formData);
    setShowEditModal(false);
    setSelectedProposal(null);
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="animate-pulse space-y-4">
      {[1, 2].map((i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex justify-between items-start mb-3">
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 rounded w-32"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
            <div className="flex space-x-2">
              <div className="h-8 bg-gray-200 rounded w-16"></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-48"></div>
            <div className="h-4 bg-gray-200 rounded w-40"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <div className="text-center">
        <div className="font-bold text-2xl">
          <div className="text-black">No Open</div>
          <div className="text-[#0D9488]">Proposals</div>
        </div>
        <p className="text-gray-600 mt-4 mb-6">Create a new proposal to play tennis with your group members.</p>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Propose New Play
        </button>
      </div>
    </div>
  );

  return (
    <section>
      <div className="bg-primary/5 py-3 px-4 mb-6">
        <h2 className="text-xl font-bold text-textDark">Your Open Proposals</h2>
      </div>
      
      <div className="space-y-4">
        {isLoading ? (
          <LoadingSkeleton />
        ) : proposals.length === 0 ? (
          <EmptyState />
        ) : (
          proposals.slice(0, 5).map(proposal => (
            <div
              key={proposal.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg text-textDark">
                    {proposal.date} at {formatTime(proposal.time)}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs ${proposal.type === PLAY_TYPES.MATCH ? "bg-purple-100 text-purple-800" : "bg-orange-100 text-orange-800"}`}>
                      {proposal.type === PLAY_TYPES.MATCH ? "Match" : "Practice"}
                    </span>
                    {proposal.type === PLAY_TYPES.MATCH && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                        {proposal.format}
                      </span>
                    )}
                    {proposal.proposerSkillLevel && (
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                        NTRP {proposal.proposerSkillLevel}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  {proposal.acceptedCount === 0 && (
                    <button
                      onClick={() => handleEdit(proposal)}
                      className="px-3 py-1 text-sm text-primary bg-primary/5 hover:bg-primary/10 rounded-md transition"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteClick(proposal)}
                    className="px-3 py-1 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>{proposal.groupName}</span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{proposal.location}</span>
                </div>

                {(proposal.format === PLAY_FORMATS.DOUBLES || proposal.type === PLAY_TYPES.PRACTICE) && (
                  <div className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>
                      {proposal.format === PLAY_FORMATS.DOUBLES && (
                        `${proposal.acceptedCount}/4 players (teams formed at courts)`
                      )}
                      {proposal.type === PLAY_TYPES.PRACTICE && (
                        <>
                          <span>{proposal.acceptedCount}/{proposal.maxPlayers} players</span>
                          <div className="text-xs text-gray-500 mt-1">
                            {proposal.acceptedCount < proposal.minPlayers 
                              ? `Need ${proposal.minPlayers - proposal.acceptedCount} more to hit minimum for practice` 
                              : proposal.acceptedCount < proposal.maxPlayers 
                                ? `Can add ${proposal.maxPlayers - proposal.acceptedCount} more players`
                                : 'Full'}
                          </div>
                          {proposal.acceptedCount >= proposal.minPlayers && (
                            <div className="text-xs text-green-600 mt-1">
                              ✓ Minimum players reached - Practice will happen!
                            </div>
                          )}
                        </>
                      )}
                      {proposal.format === PLAY_FORMATS.SINGLES && (
                        `${proposal.acceptedCount}/2 players needed`
                      )}
                    </span>
                  </div>
                )}

                {proposal.acceptedPlayers?.length > 0 && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Joined:</span> {proposal.acceptedPlayers.join(', ')}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-lg">
            <div className="text-center">
              <h3 className="text-xl font-bold mb-2 text-textDark">Delete Proposal?</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this proposal?
                <br />
                <span className="text-sm text-gray-500">This action cannot be undone.</span>
              </p>
              {error && (
                <p className="text-red-600 mb-4">{error}</p>
              )}
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setProposalToDelete(null);
                    setError(null);
                  }}
                  className="px-6 py-2 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary/5 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-6 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {selectedProposal && (
        <ProposePlayModal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProposal(null);
          }}
          onSubmit={handleEditSubmit}
          formData={selectedProposal}
          onChange={(e) => {
            const { name, value } = e.target;
            setSelectedProposal(prev => ({
              ...prev,
              [name]: value
            }));
          }}
          groupOptions={[{ name: selectedProposal.groupName }]}
          isEditing={true}
        />
      )}
    </section>
  );
}

OpenProposalsSection.propTypes = {
  proposals: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      type: PropTypes.oneOf(Object.values(PLAY_TYPES)).isRequired,
      format: PropTypes.oneOf(Object.values(PLAY_FORMATS)),
      date: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      groupName: PropTypes.string.isRequired,
      acceptedCount: PropTypes.number.isRequired,
      maxPlayers: PropTypes.number,
      minPlayers: PropTypes.number,
      acceptedPlayers: PropTypes.arrayOf(PropTypes.string),
      proposerSkillLevel: PropTypes.string
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  onProposalDeleted: PropTypes.func
}; 